import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, CalendarPlus, ExternalLink, Loader2, ShieldX } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { AdminLayout } from '@/app/shell/admin-layout';
import { useAuditActions, type AdminAction } from '@/shared/api/admin-audit';
import {
  useAdminSubscription,
  useExpireSubscription,
  useExtendSubscription,
  type SubscriptionStatus,
} from '@/shared/api/admin-subscriptions';
import { cn } from '@/shared/lib/cn';
import { ReasonDialog } from '@/shared/ui/reason-dialog';

/**
 * Drill-down page for a single subscription.  Surfaces:
 *   • Identity (id, customer, plan, status, provider).
 *   • Period + grant attribution (current_period_start/end, granted_by/reason).
 *   • Operations:  Extend (period only), Expire (immediate cancel).  Grant
 *     additional / re-grant happens via the existing /subscriptions/grant form.
 *   • Timeline:  every audit row targeting this subscription (manual_grant,
 *     manual_expire, manual_extend, view_detail), most recent first.
 *
 * Customer name resolution happens client-side via a Link to /users/$id or
 * /teams/$id — we don't extra-fetch the customer record because the audit
 * trail already exposes the customer principal through the audit entries.
 */
export function SubscriptionDetailPage({ id }: { id: string }) {
  const navigate = useNavigate();
  const { data: sub, isLoading, error } = useAdminSubscription(id);
  const audit = useAuditActions({ targetType: 'subscription', targetId: id, limit: 100 });

  const [extendOpen, setExtendOpen] = useState(false);
  const [expireOpen, setExpireOpen] = useState(false);
  const [extendDays, setExtendDays] = useState('30');

  const extendMut = useExtendSubscription();
  const expireMut = useExpireSubscription();

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="grid place-items-center py-12 text-neutral-500">
          <Loader2 className="size-5 animate-spin" />
        </div>
      </AdminLayout>
    );
  }
  if (error || !sub) {
    return (
      <AdminLayout>
        <div className="rounded-lg border border-red-900 bg-red-950/30 p-4 text-sm text-red-300">
          Subscription not found or not accessible.
        </div>
      </AdminLayout>
    );
  }

  const customerHref =
    sub.customerType === 'user' ? `/users/${sub.customerId}` : `/teams/${sub.customerId}`;
  const canMutate = (['trial', 'active', 'lifetime'] as SubscriptionStatus[]).includes(sub.status);
  const canExtend = (['trial', 'active'] as SubscriptionStatus[]).includes(sub.status);

  return (
    <AdminLayout>
      <Link
        to="/subscriptions"
        className="mb-4 inline-flex items-center gap-1 text-sm text-neutral-400 hover:text-neutral-200"
      >
        <ArrowLeft size={14} /> All subscriptions
      </Link>

      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-100">
            {sub.planCode}
            <span className="ml-3 align-middle">
              <StatusBadge status={sub.status} />
            </span>
          </h1>
          <p className="mt-1 text-sm text-neutral-400">
            <Link to={customerHref} className="inline-flex items-center gap-1 text-orange-400 hover:underline">
              {sub.customerType === 'user' ? 'User' : 'Team'} {sub.customerId.slice(0, 8)}…
              <ExternalLink size={11} />
            </Link>
            <span className="mx-2 text-neutral-700">·</span>
            <code className="text-xs">{sub.id}</code>
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {canExtend ? (
            <Button onClick={() => setExtendOpen(true)} icon={<CalendarPlus size={14} />}>
              Extend
            </Button>
          ) : null}
          {canMutate ? (
            <Button onClick={() => setExpireOpen(true)} tone="danger" icon={<ShieldX size={14} />}>
              Expire
            </Button>
          ) : null}
        </div>
      </header>

      <section className="grid gap-4 md:grid-cols-2">
        <Card title="Period">
          <DataRow label="Current period start" value={formatDateTime(sub.currentPeriodStart)} />
          <DataRow
            label="Current period end"
            value={sub.currentPeriodEnd ? formatDateTime(sub.currentPeriodEnd) : 'lifetime'}
          />
          <DataRow
            label="Trial ends"
            value={sub.trialEndsAt ? formatDateTime(sub.trialEndsAt) : 'no trial'}
          />
          <DataRow
            label="Cancel at period end"
            value={sub.cancelAtPeriodEnd ? <span className="text-amber-400">yes</span> : 'no'}
          />
          <DataRow
            label="Cancelled at"
            value={sub.cancelledAt ? formatDateTime(sub.cancelledAt) : '—'}
          />
        </Card>

        <Card title="Billing">
          <DataRow label="Plan code" value={<code className="text-xs">{sub.planCode}</code>} />
          <DataRow label="Payment provider" value={sub.paymentProvider} />
          <DataRow label="Seat count" value={String(sub.seatCount)} />
          <DataRow
            label="Granted by"
            value={
              sub.grantedBy ? (
                <code className="text-xs">{sub.grantedBy.slice(0, 8)}…</code>
              ) : (
                <span className="text-neutral-500">automatic</span>
              )
            }
          />
          {sub.grantReason ? (
            <DataRow
              label="Grant reason"
              value={<span className="italic text-neutral-300">«{sub.grantReason}»</span>}
            />
          ) : null}
          <DataRow label="Created" value={formatDateTime(sub.createdAt)} />
        </Card>
      </section>

      <section className="mt-6">
        <h2 className="mb-3 text-sm font-medium text-neutral-300">Timeline</h2>
        {audit.isLoading ? (
          <div className="text-sm text-neutral-500">Loading…</div>
        ) : !audit.data || audit.data.items.length === 0 ? (
          <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-6 text-center text-sm text-neutral-500">
            No audit events for this subscription yet.
          </div>
        ) : (
          <ol className="space-y-2">
            {audit.data.items.map((a) => (
              <TimelineRow key={a.id} action={a} />
            ))}
          </ol>
        )}
      </section>

      {/* ── Dialogs ──────────────────────────────────────────────────────── */}
      {extendOpen ? (
        <ReasonDialog
          open={true}
          onOpenChange={(o) => {
            if (!o) {
              setExtendOpen(false);
              setExtendDays('30');
            }
          }}
          title="Extend subscription period?"
          description={`Push the period_end forward without changing plan or pricing.  Plan: ${
            sub.planCode
          } · current period ends ${
            sub.currentPeriodEnd ? new Date(sub.currentPeriodEnd).toLocaleDateString() : 'lifetime'
          }.`}
          confirmLabel={`Extend by ${extendDays || '?'} day${extendDays === '1' ? '' : 's'}`}
          tone="default"
          pending={extendMut.isPending}
          onConfirm={async (reason) => {
            const days = Number(extendDays);
            if (!Number.isFinite(days) || days <= 0 || days > 3650) {
              toast.error('Days must be a positive integer ≤ 3650');
              throw new Error('invalid_days');
            }
            const next = await extendMut.mutateAsync({ id: sub.id, additionalDays: days, reason });
            toast.success(
              `Period extended.  New end: ${
                next.currentPeriodEnd
                  ? new Date(next.currentPeriodEnd).toLocaleDateString()
                  : 'lifetime'
              }`,
            );
            setExtendOpen(false);
            setExtendDays('30');
          }}
        >
          <label className="mb-1.5 block text-xs uppercase tracking-wider text-neutral-400">
            Additional days <span className="text-neutral-600">(1–3650)</span>
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={1}
              max={3650}
              value={extendDays}
              onChange={(e) => setExtendDays(e.target.value)}
              className="w-28 rounded-md bg-neutral-800 px-3 py-2 text-sm text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500"
            />
            <span className="text-xs text-neutral-500">
              {sub.currentPeriodEnd && extendDays && Number(extendDays) > 0
                ? `→ ${new Date(
                    new Date(sub.currentPeriodEnd).getTime() +
                      Number(extendDays) * 24 * 60 * 60 * 1000,
                  ).toLocaleDateString()}`
                : sub.currentPeriodEnd === null
                  ? '(lifetime sub — extending is a no-op)'
                  : null}
            </span>
          </div>
        </ReasonDialog>
      ) : null}

      {expireOpen ? (
        <ReasonDialog
          open={true}
          onOpenChange={(o) => !o && setExpireOpen(false)}
          title="Expire subscription?"
          description={`Customer immediately downgrades to free.  Existing data is preserved (soft enforcement).  Plan was: ${sub.planCode}.`}
          confirmLabel="Expire"
          tone="danger"
          pending={expireMut.isPending}
          onConfirm={async (reason) => {
            await expireMut.mutateAsync({ id: sub.id, reason });
            toast.success('Subscription expired');
            setExpireOpen(false);
            await navigate({ to: '/subscriptions' });
          }}
        />
      ) : null}
    </AdminLayout>
  );
}

// ── primitives ──────────────────────────────────────────────────────────

function Button(props: {
  onClick: () => void;
  children: React.ReactNode;
  tone?: 'default' | 'danger';
  icon?: React.ReactNode;
}) {
  const toneClasses =
    props.tone === 'danger'
      ? 'bg-red-700 text-white hover:bg-red-800'
      : 'bg-neutral-800 text-neutral-200 hover:bg-neutral-700';
  return (
    <button
      onClick={props.onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium ring-1 ring-neutral-800 transition-colors',
        toneClasses,
      )}
    >
      {props.icon}
      {props.children}
    </button>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-neutral-800 bg-neutral-900 p-5">
      <h2 className="mb-3 text-sm font-medium text-neutral-300">{title}</h2>
      <dl className="space-y-2">{children}</dl>
    </section>
  );
}

function DataRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 text-sm">
      <dt className="text-neutral-500">{label}</dt>
      <dd className="text-right text-neutral-200">{value}</dd>
    </div>
  );
}

function StatusBadge({ status }: { status: SubscriptionStatus }) {
  const tone = {
    free: 'bg-neutral-800 text-neutral-400 ring-neutral-700',
    trial: 'bg-blue-500/15 text-blue-300 ring-blue-500/30',
    active: 'bg-emerald-500/15 text-emerald-300 ring-emerald-500/30',
    past_due: 'bg-amber-500/15 text-amber-300 ring-amber-500/30',
    cancelled: 'bg-neutral-700/30 text-neutral-400 ring-neutral-700',
    lifetime: 'bg-orange-500/15 text-orange-300 ring-orange-500/30',
  }[status];
  return (
    <span
      className={cn(
        'inline-block rounded-full px-2 py-0.5 text-[10px] font-medium uppercase ring-1',
        tone,
      )}
    >
      {status}
    </span>
  );
}

function TimelineRow({ action }: { action: AdminAction }) {
  return (
    <li className="rounded-lg border border-neutral-800 bg-neutral-900 px-4 py-3">
      <div className="flex items-start justify-between gap-3 text-sm">
        <div className="min-w-0">
          <div className="font-medium text-neutral-100">
            {humaniseActionKind(action.actionKind)}
            {action.impersonationActorId ? (
              <span className="ml-2 inline-block rounded-full bg-purple-500/15 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-purple-300 ring-1 ring-purple-500/30">
                via impersonation
              </span>
            ) : null}
          </div>
          <div className="mt-0.5 text-xs text-neutral-500">
            actor: <code>{action.actorUserId.slice(0, 8)}…</code>
            {action.reason ? (
              <>
                <span className="mx-2 text-neutral-700">·</span>
                <span className="italic text-neutral-400">«{action.reason}»</span>
              </>
            ) : null}
          </div>
          {Object.keys(action.metadata ?? {}).length > 0 ? (
            <pre className="mt-1 overflow-x-auto rounded bg-neutral-950 px-2 py-1 text-[11px] text-neutral-400">
              {JSON.stringify(action.metadata, null, 0)}
            </pre>
          ) : null}
        </div>
        <time className="shrink-0 text-xs text-neutral-500" title={action.occurredAt}>
          {formatRelative(action.occurredAt)}
        </time>
      </div>
    </li>
  );
}

function humaniseActionKind(kind: string): string {
  switch (kind) {
    case 'subscription.manual_grant':
      return 'Subscription granted';
    case 'subscription.manual_expire':
      return 'Subscription expired';
    case 'subscription.manual_extend':
      return 'Period extended';
    case 'subscription.view_detail':
      return 'Subscription viewed';
    default:
      return kind;
  }
}

function formatDateTime(iso: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function formatRelative(iso: string): string {
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
